import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_pharma-compliance-7/artifacts/2ycn5ziy_Gemini_Generated_Image_9vta8x9vta8x9vta-Photoroom.png';

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const aboutTimer = useRef(null);
  const servicesTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setAboutOpen(false);
    setServicesOpen(false);
  }, [location]);

  // Close dropdowns when clicking outside (desktop only)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-dropdown]') && !e.target.closest('[data-testid="mobile-menu"]')) {
        setAboutOpen(false);
        setServicesOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Auto-close after 6 seconds
  const toggleAbout = useCallback(() => {
    const next = !aboutOpen;
    setAboutOpen(next);
    setServicesOpen(false);
    clearTimeout(aboutTimer.current);
    if (next) aboutTimer.current = setTimeout(() => setAboutOpen(false), 6000);
  }, [aboutOpen]);

  const toggleServices = useCallback(() => {
    const next = !servicesOpen;
    setServicesOpen(next);
    setAboutOpen(false);
    clearTimeout(servicesTimer.current);
    if (next) servicesTimer.current = setTimeout(() => setServicesOpen(false), 6000);
  }, [servicesOpen]);

  const aboutItems = [
    { label: 'Our Story', path: '/about#story' },
    { label: 'Our Team', path: '/about#team' },
    { label: 'Vision & Mission', path: '/about#vision' },
    { label: 'Quality Policy', path: '/about#quality' },
  ];

  const servicesItems = [
    { label: 'FDA Regulatory Compliance', path: '/services/fda' },
    { label: 'CDSCO Regulatory Compliance', path: '/services/cdsco' },
    { label: 'Global Regulatory Compliance', path: '/services/global-regulatory' },
    { label: 'Revised Schedule M GMP Advisory', path: '/services/gmp' },
    { label: 'WHO GMP Certification Advisory', path: '/services/who-gmp' },
    { label: 'Specialized Regulatory Consultation', path: '/services/specialized' },
    { label: 'Facility Design & Project Consultancy', path: '/services/facility-design' },
    { label: 'Turnkey Key Project', path: '/services/turnkey' },
    { label: 'Global Drug Sourcing', path: '/services/drug-sourcing' },
  ];

  const NavLink = ({ to, children }) => (
    <Link
      to={to}
      className="text-sm font-bold hover:text-blue-100 transition-colors relative group whitespace-nowrap text-white"
    >
      {children}
      <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-white transition-all duration-200 group-hover:w-full" />
    </Link>
  );

  const DropdownTrigger = ({ children, open, onEnter, onLeave }) => (
    <button
      onMouseEnter={onEnter}
      className="text-sm font-bold hover:text-blue-100 transition-colors flex items-center gap-1 whitespace-nowrap text-white"
    >
      {children}
      <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
  );

  const DropdownMenu = ({ items, wide }) => (
    <div className={`absolute top-full left-0 mt-2 ${wide ? 'w-60' : 'w-52'} bg-white rounded-xl border border-[#BFDBFE] shadow-xl py-1.5 z-50`}>
      {items.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className="block px-4 py-2 text-sm hover:bg-[#EFF6FF] hover:text-[#1570EF] border-l-[3px] border-transparent hover:border-[#1570EF] transition-all duration-150"
          style={{ color: '#1B3A6B' }}
        >
          {item.label}
        </Link>
      ))}
    </div>
  );

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 ${scrolled ? 'shadow-lg' : ''}`}
      style={{ height: '72px', backgroundColor: '#1570EF', boxShadow: scrolled ? '0 2px 20px rgba(21,112,239,0.15)' : 'none' }}
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0" data-testid="logo-link">
          <img src={LOGO_URL} alt="Pegasus Clinicare" className="h-14 w-auto" />
        </Link>

        {/* Desktop Nav - only key items */}
        <nav className="hidden xl:flex items-center gap-6">
          <NavLink to="/">Home</NavLink>

          {/* About Dropdown */}
          <div className="relative" data-dropdown>
            <button onClick={toggleAbout} data-testid="nav-about-dropdown"
              className="text-sm font-bold hover:text-blue-100 transition-colors flex items-center gap-1 whitespace-nowrap text-white">
              About Us
              <ChevronDown size={14} className={`transition-transform ${aboutOpen ? 'rotate-180' : ''}`} />
            </button>
            {aboutOpen && <DropdownMenu items={aboutItems} />}
          </div>

          {/* Services Dropdown */}
          <div className="relative" data-dropdown>
            <button onClick={toggleServices} data-testid="nav-services-dropdown"
              className="text-sm font-bold hover:text-blue-100 transition-colors flex items-center gap-1 whitespace-nowrap text-white">
              Services
              <ChevronDown size={14} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {servicesOpen && <DropdownMenu items={servicesItems} wide />}
          </div>

          <NavLink to="/industries">Industries</NavLink>
          <NavLink to="/why-pegasus">Why Pegasus</NavLink>
          <NavLink to="/clients">Clients</NavLink>
          <NavLink to="/contact">Contact</NavLink>
        </nav>

        {/* CTA + Hamburger */}
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="cta-consultation-button"
            className="hidden sm:block px-5 py-2.5 bg-white text-[#1570EF] text-sm font-bold rounded-full hover:bg-blue-50 transition-all duration-200"
          >
            Free Consultation
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden p-2 rounded-lg hover:bg-white/20 transition-colors text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Slide-in Menu (mobile + tablet) */}
      {mobileMenuOpen && (
        <>
          <div className="fixed inset-0 top-[72px] bg-black/30 z-30" onClick={() => setMobileMenuOpen(false)} />
          <div
            data-testid="mobile-menu"
            className="fixed right-0 top-[72px] bottom-0 w-80 max-w-[85vw] bg-white shadow-2xl z-40 overflow-y-auto border-l border-gray-100"
          >
            <nav className="p-6 space-y-1">
              <MobileLink to="/">Home</MobileLink>

              {/* About accordion */}
              <div data-dropdown>
                <button
                  onClick={(e) => { e.stopPropagation(); setAboutOpen(!aboutOpen); }}
                  className="w-full flex items-center justify-between py-3 text-base font-medium border-b border-gray-50"
                  style={{ color: '#1B3A6B' }}
                >
                  About Us
                  <ChevronDown size={16} className={`transition-transform ${aboutOpen ? 'rotate-180' : ''}`} />
                </button>
                {aboutOpen && (
                  <div className="pl-4 py-1 space-y-0.5">
                    {aboutItems.map((item) => (
                      <Link key={item.path} to={item.path} className="block py-2 text-sm text-gray-600 hover:text-[#1570EF]">
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Services accordion */}
              <div data-dropdown>
                <button
                  onClick={(e) => { e.stopPropagation(); setServicesOpen(!servicesOpen); }}
                  className="w-full flex items-center justify-between py-3 text-base font-medium border-b border-gray-50"
                  style={{ color: '#1B3A6B' }}
                >
                  Services
                  <ChevronDown size={16} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {servicesOpen && (
                  <div className="pl-4 py-1 space-y-0.5">
                    {servicesItems.map((item) => (
                      <Link key={item.path} to={item.path} className="block py-2 text-sm text-gray-600 hover:text-[#1570EF]">
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <MobileLink to="/industries">Industries</MobileLink>
              <MobileLink to="/why-pegasus">Why Pegasus</MobileLink>
              <MobileLink to="/clients">Clients</MobileLink>
              <MobileLink to="/knowledge-hub">Knowledge Hub</MobileLink>
              <MobileLink to="/contact">Contact Us</MobileLink>

              <div className="pt-4">
                <Link
                  to="/contact"
                  className="block w-full text-center px-6 py-3 bg-[#1570EF] text-white font-semibold rounded-full hover:bg-[#1E40AF] transition-all"
                >
                  Free Consultation
                </Link>
              </div>
            </nav>
          </div>
        </>
      )}
    </header>
  );
};

const MobileLink = ({ to, children }) => (
  <Link
    to={to}
    className="block py-3 text-base font-medium border-b border-gray-50 hover:text-[#1570EF]"
    style={{ color: '#1B3A6B' }}
  >
    {children}
  </Link>
);

export default Header;
